import { useMemo, useState } from "react";

import { buildChartDataByType } from "../..";
import { ErrorContent } from "../../../ErrorContent";
import { useIsMobile } from "../../../hooks";
import { ResponsiveSpinner } from "../../../ResponsiveSpinner";
import { ToggleButton } from "../../Common/ToggleButton";
import {
  AccountTypeCount,
  ChartTypeOption,
  ClientsPageAnalytics,
  ReturnedTranslatedContentClientsPageHook,
  TranslatedContent,
} from "../../types";
import { useClientAccountReportContext } from "../providers/AccountReportProvider";
import { ChartPie } from "./ChartPie";
import { ChartSectionEntry } from "./ChartSectionEntry";
import { ChartSectionEntryMini } from "./ChartSectionEntryMini";

type ClientAccountReportChartsSection = {
  analytics?: ClientsPageAnalytics;
  translations: ReturnedTranslatedContentClientsPageHook;
};

export const ClientAccountReportChartsSection = ({
  translations,
  analytics,
}: ClientAccountReportChartsSection) => {
  const isMobile = useIsMobile();
  const {
    resources: { stats },
    runResource,
  } = useClientAccountReportContext();
  const { error, status } = stats;
  const isLoading = status === "loading";
  // Matches TradingReportChartsLoading -- just a spinner in a brand-subtle
  // card, sized the same as the page-level loading skeleton (h-[422px]/
  // h-[101px]) so nothing jumps between the two. mb-0/my-10 mirrors the
  // loaded ClientAccountReportChartsV2 wrapper's own margin below.
  if (isLoading) {
    return (
      <div
        className={`flex items-center justify-center rounded-2xl bg-surface-brand-subtle ${
          isMobile ? "h-[101px]" : "my-10 h-[422px]"
        }`}
      >
        <ResponsiveSpinner
          width={isMobile ? 24 : 40}
          height={isMobile ? 24 : 40}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-4 mt-10 flex w-full flex-col items-center bg-surface-brand-subtle p-8">
        <ErrorContent action={() => runResource("stats")} kind="chart" />
      </div>
    );
  }

  if (!stats || !stats.data) {
    return null;
  }

  return (
    <ClientAccountReportChartsV2
      isMobile={isMobile}
      analytics={analytics}
      translations={translations.translatedContent}
      data={stats.data!}
    />
  );
};

type ClientAccountReportChartsProps = {
  isMobile?: boolean;
  translations: TranslatedContent;
  analytics?: ClientsPageAnalytics;
  data: {
    platformAccounts: AccountTypeCount[];
    accountsType: AccountTypeCount[];
    total: number;
  };
};
export const ClientAccountReportChartsV2 = ({
  isMobile,
  analytics,
  translations,
  data,
}: ClientAccountReportChartsProps) => {
  const chartTypeOptions = useMemo<ChartTypeOption[]>(() => {
    return [
      {
        id: "accountsType",
        label: translations.accountReportTab.graphs.accountTypeTab,
      },
      {
        id: "platformAccounts",
        label: translations.accountReportTab.graphs.platformTypeTab,
      },
    ];
  }, [translations]);

  const [tab, setTab] = useState<ChartTypeOption>(chartTypeOptions[0]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return buildChartDataByType(tab.id, data);
  }, [data, tab]);

  const subTitle = useMemo(() => {
    if (tab.id === "accountsType") {
      return translations.accountReportTab.graphs.accountTypeTabTitle;
    }
    if (tab.id === "platformAccounts") {
      return translations.accountReportTab.graphs.platformTypeTabTitle;
    }
  }, [tab, translations]);

  if (!chartData || chartData.length === 0) {
    return null;
  }

  return (
    <div data-testid="charts-section" className="mb-0 lg:my-10">
      <ChartSectionEntry
        isMobile={isMobile}
        title={translations.accountReportTab.graphs.title}
        subtitle={subTitle}
        chart={
          <ChartPie
            translations={translations}
            data={chartData}
            analytics={analytics}
          />
        }
        chartMini={
          <ChartSectionEntryMini data={chartData} translations={translations} />
        }
        filterControls={
          <ToggleButton<ChartTypeOption>
            items={chartTypeOptions}
            selectedValue={tab}
            onClick={setTab}
          />
        }
      />
    </div>
  );
};
